import React, { useCallback, useEffect } from 'react';

const VehicleForm = ({
  vehicleCategories = [],
  vehicleData,
  onChange,
  onSubmit,
  onBack,
  cmsStates,
  filteredCities,
  filteredVehicleMakers,
  filteredModels,
  locationSuggestionState,
  pincodeSuggestionList,
  onLocationSuggestion,
  onLocationSelection,
  setLocationSuggestionState,
  pushToDataLayer,
}) => {
  const selectedCategory = vehicleCategories.find(
    (cat) => cat.displayName === vehicleData.vehicleCategory
  );

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      onChange({ [name]: value });

      // Track vehicle selections
      if (value) {
        let eventAction = '';
        let eventLabel = value;

        switch (name) {
          case 'vehicleCategory':
            eventAction = 'Vehicle Category';
            break;
          case 'vehicleType':
            eventAction = 'Vehicle Type';
            break;
          case 'vehicleMaker':
            eventAction = 'Vehicle Maker';
            break;
          case 'vehicleModel':
            eventAction = 'Vehicle Model';
            break;
          case 'yearOfPurchase':
            eventAction = 'Year Of Purchase';
            eventLabel = value;
            break;
          default:
            return null;
        }

        pushToDataLayer({
          event: 'Enquiry_Form',
          eventCategory: 'Enquiry form',
          eventAction: eventAction,
          eventLabel: eventLabel,
          FormType: 'ELV Form',
          section_name: 'Vehicle Details',
        });
      }
    },
    [onChange, vehicleData, pushToDataLayer]
  );

  const handleLocationChange = useCallback(
    (e) => {
      onLocationSuggestion(e.target.value);
    },
    [onLocationSuggestion]
  );

  const handleLocationSelect = useCallback(
    (item) => {
      onLocationSelection(item);

      // Track pincode selection using NEW schema
      pushToDataLayer({
        event: 'Enquiry_Form',
        eventCategory: 'Enquiry form',
        eventAction: 'Pincode/Location',
        eventLabel: item.name, // Pass the selected location
        FormType: 'ELV Form',
        section_name: 'Vehicle Details',
      });
    },
    [onLocationSelection, pushToDataLayer]
  );

  useEffect(() => {
    const vehicleTypes = selectedCategory?.fields?.Vehicles || [];

    if (vehicleTypes.length && !vehicleData.vehicleType) {
      onChange({
        vehicleType: vehicleTypes[0].displayName,
      });
    }
  }, [selectedCategory, vehicleData.vehicleType, onChange]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedInsideSuggestion = e.target.closest('.location-suggestion-container');
      const clickedInsideInput = e.target.closest('#pincode');

      if (!clickedInsideSuggestion && !clickedInsideInput) {
        setLocationSuggestionState(false);

        if (pincodeSuggestionList.length > 0) {
          onChange({
            pincode: '',
          });
        }
      }
    };

    if (locationSuggestionState) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [locationSuggestionState, setLocationSuggestionState, pincodeSuggestionList, onChange]);

  return (
    <form className="contact-form" onSubmit={onSubmit}>
      <div className="form-scroll">
        <div className="form-group floating-label">
          <select
            id="vehicleCategory"
            name="vehicleCategory"
            value={vehicleData.vehicleCategory}
            onChange={handleChange}
            className="select-input"
            required
          >
            <option value="" disabled>
              Select Vehicle Category
            </option>

            {vehicleCategories.map((category) => (
              <option key={category.id} value={category.displayName}>
                {category.displayName}
              </option>
            ))}
          </select>
          <label htmlFor="vehicleCategory">
            Vehicle Category<span className="required">*</span>
          </label>
        </div>

        <div className="form-group">
          <label>
            Vehicle Type<span className="required">*</span>
          </label>
          <div className="radio-group">
            {(selectedCategory?.fields?.Vehicles || []).map((vehicle) => (
              <label key={vehicle.id} className="radio-option">
                <input
                  type="radio"
                  placeholder="vehicleType"
                  name="vehicleType"
                  value={vehicle.displayName}
                  checked={vehicleData.vehicleType === vehicle.displayName}
                  onChange={handleChange}
                  disabled={!vehicleData.vehicleCategory}
                />
                <span>{vehicle.displayName}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group floating-label">
          <select
            id="vehicleMaker"
            name="vehicleMaker"
            value={vehicleData.vehicleMaker}
            onChange={handleChange}
            className="select-input"
            required
            disabled={!filteredVehicleMakers.length}
          >
            <option value="" disabled>
              Select Vehicle Maker
            </option>
            {filteredVehicleMakers.map((maker) => (
              <option key={maker.id} value={maker.displayName}>
                {maker.displayName}
              </option>
            ))}
          </select>
          <label htmlFor="vehicleMaker">
            Vehicle Maker<span className="required">*</span>
          </label>
        </div>

        <div className="form-group floating-label">
          <select
            id="vehicleModel"
            name="vehicleModel"
            value={vehicleData.vehicleModel}
            onChange={handleChange}
            className="select-input"
            required
            // disabled={!filteredModels.length}
          >
            <option value="">Select Vehicle Model</option>
            {filteredModels.length > 0 ? (
              filteredModels.map((model) => (
                <option key={model.id} value={model.fields.VehicleModel.value}>
                  {model.fields.VehicleModel.value}
                </option>
              ))
            ) : (
              <option value="Other">Other</option>
            )}
          </select>
          <label htmlFor="vehicleModel">
            Vehicle Model<span className="required">*</span>
          </label>
        </div>

        <div className="form-group floating-label">
          <input
            type="text"
            id="pincode"
            name="pincode"
            placeholder="Pincode/Location"
            value={vehicleData.pincode}
            onChange={handleLocationChange}
            required
          />
          <label htmlFor="pincode">
            Pincode/Location<span className="required">*</span>
          </label>
          {locationSuggestionState && pincodeSuggestionList.length > 0 && (
            <div className="location-suggestion-container">
              {pincodeSuggestionList.map((item) => (
                <div
                  key={item.name}
                  className="location-suggestion-item"
                  onClick={() => handleLocationSelect(item)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleLocationSelect(item)}
                >
                  {item?.name}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-group floating-label date-input">
          <input
            type="date"
            id="yearOfPurchase"
            placeholder="Year Of Purchase"
            name="yearOfPurchase"
            value={vehicleData.yearOfPurchase}
            onChange={handleChange}
            max={new Date().toISOString().split('T')[0]}
          />

          <label htmlFor="yearOfPurchase">Year Of Purchase</label>
        </div>
      </div>
      <div className="form-actions">
        <button type="button" className="button-secondary" onClick={onBack}>
          Back
        </button>
        <button
          type="submit"
          className={`button-primary ${
            vehicleData.vehicleCategory &&
            vehicleData.vehicleType &&
            vehicleData.vehicleMaker &&
            vehicleData.vehicleModel &&
            vehicleData.pincode?.trim()
              ? 'enabled'
              : ''
          }`}
          disabled={
            !(
              vehicleData.vehicleCategory &&
              vehicleData.vehicleType &&
              vehicleData.vehicleMaker &&
              vehicleData.vehicleModel &&
              vehicleData.pincode?.trim()
            )
          }
        >
          Next
        </button>
      </div>
    </form>
  );
};

export default VehicleForm;
